// components/orders/OrderCard.jsx
import React from 'react';
import { ShoppingBag, ChevronDown, ChevronUp, Store, Printer } from 'lucide-react';
import OrderDeliveryInfo from './OrderDeliveryInfo';
import OrderItemsTable from './OrderItemsTable';
import OrderSettlementInfo from './OrderSettlementInfo';

const STATUS_CONFIG = {
  pending:    { label: '待指派',  color: 'bg-yellow-100 text-yellow-700' },
  assigned:   { label: '待接单',  color: 'bg-blue-100 text-blue-700' },
  confirmed:  { label: '已接单',  color: 'bg-indigo-100 text-indigo-700' },
  preparing:  { label: '备货中',  color: 'bg-orange-100 text-orange-700' },
  delivering: { label: '配送中',  color: 'bg-purple-100 text-purple-700' },
  ready:      { label: '待自提',  color: 'bg-cyan-100 text-cyan-700' },
  completed:  { label: '已完成',  color: 'bg-green-100 text-green-700' },
  cancelled:  { label: '已取消',  color: 'bg-gray-100 text-gray-500' }
};

const PAYMENT_METHOD_LABEL = { khqr: 'KHQR', alipay: '支付寶', usdt: 'USDT', cod: '貨到付款' };

const OrderCard = ({ order, isExpanded, isHQ, isManager, onToggle, onAssign, onAction, onPrint }) => {
  const statusCfg = STATUS_CONFIG[order.order_status] || STATUS_CONFIG.pending;

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div
        className="p-5 flex items-start justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => onToggle(order.id)}
      >
        <div className="flex items-start space-x-4">
          <div className="bg-blue-100 p-2 rounded-lg mt-0.5">
            <ShoppingBag className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center space-x-2 flex-wrap gap-1">
              <span className="font-semibold">{order.order_no}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                order.delivery_type === 'pickup' ? 'bg-cyan-100 text-cyan-700' : 'bg-purple-100 text-purple-700'
              }`}>
                {order.delivery_type === 'pickup' ? '🏪 自提' : '🛵 配送'}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                order.payment_status === 'paid'
                  ? 'bg-green-100 text-green-700'
                  : order.payment_method === 'cod'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-100 text-red-600'
              }`}>
                {order.payment_method === 'cod'
                  ? '货到付款'
                  : order.payment_status === 'paid'
                    ? `${PAYMENT_METHOD_LABEL[order.payment_method] || order.payment_method} 已付款`
                    : `${PAYMENT_METHOD_LABEL[order.payment_method] || order.payment_method} 待付款`}
              </span>
            </div>
            <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500 flex-wrap gap-1">
              <span>{order.customer_name || '用户'} {order.customer_phone}</span>
              <span>${parseFloat(order.total_amount).toFixed(2)}</span>
              <span>{new Date(order.created_at).toLocaleDateString('zh-CN')}</span>
            </div>
            {order.delivery_type === 'delivery' && order.delivery_address && (
              <p className="text-xs text-gray-500 mt-1 truncate max-w-md">
                📍 {order.delivery_address}
              </p>
            )}
            {order.assigned_store_name && (
              <p className="text-xs text-blue-600 mt-1 flex items-center space-x-1">
                <Store className="w-3 h-3" />
                <span>指派：{order.assigned_store_name}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-2" onClick={e => e.stopPropagation()}>
          {isHQ && order.order_status === 'pending' && order.delivery_type === 'delivery' && (
            <button onClick={() => onAssign(order)}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
              指派配送
            </button>
          )}
          {isHQ && order.order_status === 'pending' && order.delivery_type === 'pickup' && (
            <button onClick={() => onAction('confirm', order)}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
              确认接单
            </button>
          )}
          {isManager && !isHQ && order.order_status === 'assigned' && (
            <button onClick={() => onAction('confirm', order)}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
              接单
            </button>
          )}
          {isHQ && order.order_status === 'assigned' && (
            <button onClick={() => onAction('confirm', order)}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
              确认接单
            </button>
          )}
          {order.order_status === 'confirmed' && (
            <button onClick={() => onAction('prepare', order)}
              className="bg-orange-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-orange-600">
              开始备货
            </button>
          )}
          {order.order_status === 'preparing' && (
            <button onClick={() => onAction('deliver', order)}
              className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
              {order.delivery_type === 'pickup' ? '通知自提' : '开始配送'}
            </button>
          )}
          {(order.order_status === 'delivering' || order.order_status === 'ready') && (
            <button onClick={() => onAction('complete', order)}
              className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
              确认完成
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); onPrint(order); }}
            className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1">
            <Printer className="w-3.5 h-3.5" />
            打印面單
          </button>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="border-t px-5 py-4 bg-gray-50 space-y-4">
          <OrderDeliveryInfo order={order} />
          <OrderItemsTable order={order} />
          <OrderSettlementInfo order={order} />
          {order.notes && <p className="text-sm text-gray-500">备注：{order.notes}</p>}
        </div>
      )}
    </div>
  );
};

export default OrderCard;